

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if

import Head from "next/head";
import { RequestProvider } from "../../../../context/RequestContext";
import { countryList } from "../../../../utils/constants";
import { AnimatePresence } from "framer-motion";
import { AirconItemSelection } from "../../../../components/index/service-category/aircon-service/AirconItemSelection";
import { useRouter } from "next/router";
import { getArticleDateTime, reviewedIso } from "../../../../utils/article-date-time";
import { airconCostCalculation } from "../../../../js/aircon-cost";
import { useCallback, useEffect, useState } from "react";
import { getAppSettings } from "../../../../api/app-setting/app-setting";
import Script from "next/script";
import { openModal } from "../../../../service/modal-service";
import { getRequestJSON } from "../../../../api/index/location-api";
import { Locality } from "../../../../components/index/commons/Locality";
import { AirconDealItem, AIRCON_DEAL_FILTERS } from "../../../../components/index/service-category/aircon-service/AirconDealItem";
import { airconDeals } from "../../../../utils/airconDeals";
import { Faq } from "../../../../components/commons/Faq";
import { buildLocalityFaqs } from "../../../../utils/faq";
import Link from "next/link";
import { AirconScope } from "../../../../components/index/service-category/aircon-service/AirconScope";
import { LoadingCover } from "../../../../components/commons/LoadingCover";
import { SystemError } from "../../../../components/error/SystemError";
import { getAirconChemicalSlugData, getAirconGeneralSlugData, getAirconOverhaulSlugData, setAirconChemicalSlugData, setAirconGeneralSlugData, setAirconOverhaulSlugData } from "../../../../service/page-slug-store";
import { AirconCustomerReviews } from "../../../../components/index/service-category/aircon-service/AirconCustomerReviews";
import AirconRequestSection from "@/index/service-category/aircon-service/AirconRequestSection";
import { AcServiceLocality } from "@/index/service-category/aircon-service/AcServiceLocality";
import { AirconQuickLinks } from "@/index/service-category/aircon-service/AirconQuickLinks";
import { getTestimonials } from "api/user/profile-api";
import { getAcTestimonials, setAcTestimonials } from "service/testimonial-store";
import { HiShieldCheck } from "react-icons/hi";
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'
import { buildAirconOfferCatalog } from 'utils/aircon-offers'
import { hasAirconLocalityStats, getLocalityStatsRow, getBusiestLocalities } from "data/aircon-locality-stats";
import { loadAirconLocalityStats } from "utils/load-aircon-locality-stats";
import { getNearbyLocalities } from "utils/aircon-locality-copy";
import { AirconLocalityInsights } from "@/index/service-category/aircon-service/AirconLocalityInsights";

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

// revalidation is enabled and a new request comes in
export async function getStaticProps({params}) {
    const locality = params.locality
    let lat = 1.3521
    let lng = 103.8198

    const { month, year } = getArticleDateTime();
    const modifiedDate = reviewedIso(CONTENT_REVIEWED);

    const countryCd = "sg"
    const countryName = "Singapore";
    const currencyCd = "SGD";
    let localityDetailsList;
    let localityDetails = {"name": "", "description": ""};

    const allRequestCategorySettings = await getRequestJSON(countryName)

    const testimonials = getAcTestimonials().length > 0 ? getAcTestimonials() : await getTestimonials("Aircon Services", 0, 6)
    setAcTestimonials(testimonials)
    
    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")

    const getCountryDetails = () => {
        for(let x in countryList){
            if(countryCd === countryList[x].country_cd){
                localityDetailsList = countryList[x].localityListDetails
                return
            }
        }
    }

    getCountryDetails()

    const getLocalityDetails = () => {
        for(let x in localityDetailsList){
            if(locality === localityDetailsList[x].name){
                localityDetails = localityDetailsList[x]
                lat = localityDetails.lat
                lng = localityDetails.lng
                return
            }
        }
    } 
    
    getLocalityDetails()
    
    let standardServicing = []
    let annualServicing = []
    let chemicalServicing = []
    let overhaulServicing = []    

    if(getAirconGeneralSlugData().length > 0 && getAirconChemicalSlugData().length > 0 && getAirconOverhaulSlugData().length > 0){
        standardServicing = getAirconGeneralSlugData()
        chemicalServicing = getAirconChemicalSlugData()
        overhaulServicing = getAirconOverhaulSlugData()
    } else {
        airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)
        setAirconGeneralSlugData(standardServicing)
        setAirconChemicalSlugData(chemicalServicing)
        setAirconOverhaulSlugData(overhaulServicing)  
    }
    
    const getLocalityList = () => {
        for(let x in countryList){
            if(countryList[x].country_name === countryName){
                return countryList[x].localityList
            }
        }
    } 

    const localityList = getLocalityList()

    const stats = await loadAirconLocalityStats()
    const hasStats = hasAirconLocalityStats(stats)

    return {
        props: {
            localityStats: getLocalityStatsRow(stats, locality),
            singaporeStats: hasStats ? stats.singapore : null,
            statsWindowStart: hasStats ? stats.windowStart : null,
            statsAsOf: hasStats ? stats.asOf : null,
            nearbyLocalities: getNearbyLocalities(locality),
            isAmongBusiest: getBusiestLocalities(stats).includes(locality),
            requestCategorySettings,
            countryName,
            locality,
            localityDetails,
            modifiedDate,
            month,
            year,
            standardServicing,
            annualServicing,
            chemicalServicing,
            overhaulServicing,
            currencyCd,
            testimonials,
            airconDeals,
            localityList
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every 3600 seconds
        revalidate: 86400, // In seconds
    }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
export async function getStaticPaths() {
    // Singapore localities only, and not "Singapore" itself - mirrors
    // sgLocalities() in lib/sitemap. Looping every country used to publish
    // /locality/Mumbai, /locality/New York etc. as empty "Aircon Servicing in
    // Mumbai" pages, and /locality/Singapore duplicated the hub.
    const paths = (countryList.find((c) => c.country_cd === 'sg')?.localityList ?? [])
        .filter((locality) => locality !== 'Singapore')
        .map((locality) => ({ params: { locality } }))

    // We'll pre-render only these paths at build time.
    // { fallback: blocking } will server-render pages
    // on-demand if the path doesn't exist.
    return { paths, fallback: false }
}

export default function Home(props) {
    const locality = props.locality
    const countryName = props.countryName
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const localityDetails = props.localityDetails
    const router = useRouter()
    const requestCategorySettings = props.requestCategorySettings;
    const chemicalServicing = props.chemicalServicing
    const overhaulServicing = props.overhaulServicing
    const standardServicing = props.standardServicing
    const annualServicing = props.annualServicing
    const currencyCd = props.currencyCd
    const testimonials = props.testimonials
    const dealsArray = props.airconDeals
    const localityList = props.localityList
    const localityStats = props.localityStats
    const requestType = 'Aircon Services'

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isCustomBook, setIsCustomBook ] = useState(false)
    const [ waInd, setWaInd ] = useState(false)
    const [ isShowJobScope, setIsShowJobScope ] = useState(false)
    const [ serviceType, setServiceType ] = useState("general")
    const [ unit, setUnit ] = useState("")
    const [ unitType, setUnitType ] = useState("")
    const [ cost, setCost ] = useState("")
    const [ isShowAirconItemSelection, setIsShowAirconItemSelection ] = useState(false)
    // General servicing is 70-90% of bookings in every area (see the stats block), so
    // the deals open on it rather than all 35 cards - the full set repeated verbatim
    // was the largest block of text shared by all 26 locality pages. The select still
    // offers every service.
    const [ selectedService, setSelectedService ] = useState("general")

    const [ isLoading, setIsLoading ] = useState(false)
    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""})    
    
    const fetchAppSetting = useCallback(async() => {
        try {
            const appSettings = await getAppSettings(countryName)
            setWaInd(appSettings.waBusinessInd)
        } catch (error) {
            console.log(error);
        }
    }, [countryName])

    const showJobScope = (serviceType, unitType, unit, cost) => {
        setServiceType(serviceType)
        setUnit(unit)
        setUnitType(unitType)
        setCost(cost)
        openModal(setIsShowJobScope)
    }

    useEffect(() => {
        if(!countryName) return

        fetchAppSetting()
    }, [countryName, fetchAppSetting])

    // The scroll listener that used to live here was orphaned: isShowBottomButtons was
    // never read in the JSX, and BottomButtons already owns identical logic. It was
    // re-rendering this whole page on every scroll frame past 100px.

    // One source for the URL, title and description so the canonical, the OG tags and
    // every @id in the schema agree. The schema used to build URLs from the raw
    // locality while the canonical encoded it, so "Ang Mo Kio" never matched
    // "Ang%20Mo%20Kio" and the structured data pointed at a different URL.
    const pageUrl = `https://www.stringssg.com/sg/aircon-servicing-singapore/locality/${encodeURIComponent(locality)}`
    const pageTitle = `Aircon Servicing in ${locality} from $16/unit | StringsSG`
    // $16/unit is the five-unit price with transport set aside (see
    // AirconPriceComparison), so it is never paired with "transport included".
    const pageDescription = `Aircon servicing in ${locality} with transport included: general servicing from $47.84, plus chemical wash, chemical overhaul, repair and gas top up for HDB, condo and commercial units. Technicians 8am to 10pm daily.`
    const pageImage = "https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/repairman-doing-air-conditioner-service.jpg"
    const localityLat = localityDetails?.lat ?? 1.3521
    const localityLng = localityDetails?.lng ?? 103.8198

  return (

    <>
      <Head>
        <title>{pageTitle}</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content={pageDescription}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_SG" />
        <meta property="og:url"  content={pageUrl} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={pageTitle}/>
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="og:image"  content={pageImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/>
        <meta name="twitter:url" content={pageUrl}/>
        <meta name="twitter:title" content={pageTitle}/>
        <meta name="twitter:description" content={pageDescription}/>
        <meta name="twitter:image" content={pageImage} />
        <link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"WebPage","@id":"${pageUrl}/#webpage","url":"${pageUrl}","name":"${pageTitle}","description":"${pageDescription}","inLanguage":"en-SG","primaryImageOfPage":{"@type":"ImageObject","url":"https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/repairman-doing-air-conditioner-service.jpg"},"isPartOf":{"@type":"WebSite","@id":"https://www.stringssg.com/#website","url":"https://www.stringssg.com/","name":"StringsSG"},"breadcrumb":{"@id":"${pageUrl}/#breadcrumblist"},"datePublished":"2025-01-01T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","@id":"${pageUrl}/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore","name":"Aircon Servicing Singapore","url":"https://www.stringssg.com/sg/aircon-servicing-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"${pageUrl}","name":"Aircon Servicing ${locality}","url":"${pageUrl}"}}]},{"@type":"Service","@id":"${pageUrl}/#service","name":"Aircon Servicing ${locality}","serviceType":"Aircon Servicing","description":"${pageDescription}","url":"${pageUrl}","image":"https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/repairman-doing-air-conditioner-service.jpg","provider":${JSON.stringify(localBusinessNode)},"areaServed":{"@type":"Place","name":"${locality}, Singapore","geo":{"@type":"GeoCoordinates","latitude":${localityLat},"longitude":${localityLng}}},"hasOfferCatalog":${JSON.stringify(buildAirconOfferCatalog({ name: `Aircon Services and Prices in ${locality}`, standardServicing, chemicalServicing, overhaulServicing }))}}]}`
          }}
        />
        <link rel="canonical" href={pageUrl} />
      </Head> 
        <div>
            <RequestProvider>
                <AirconRequestSection
                    requestForm={requestForm}
                    setRequestForm={setRequestForm}
                    isQuickBook={isQuickBook}
                    setIsQuickBook={setIsQuickBook}
                    isCustomBook={isCustomBook}
                    setIsCustomBook={setIsCustomBook}
                    requestCategorySettings={requestCategorySettings}
                    standardServicing={standardServicing}
                    chemicalServicing={chemicalServicing}
                    overhaulServicing={overhaulServicing}
                    currencyCd={currencyCd}
                    countryName={countryName}
                    requestType={requestType}
                    setIsShowJobScope={setIsShowJobScope}
                    serviceType={serviceType}
                    setServiceType={setServiceType}
                    setUnit={setUnit}
                    setUnitType={setUnitType}
                    setCost={setCost}
                    heroImage="blog/living/aircon/repairman-doing-air-conditioner-service.jpg"
                    title={`Aircon Servicing - ${locality}`}
                />
                <section className='max-w-5xl mx-auto py-2 px-2'>
                    <div className="leading-8 py-10 px-2">
                        <Locality localityDetails={localityDetails} heading={`Aircon Servicing in ${locality}, Singapore`}/>
                    </div>
                </section>
                <AirconLocalityInsights
                    locality={locality}
                    localityStats={localityStats}
                    singaporeStats={props.singaporeStats}
                    windowStart={props.statsWindowStart}
                    asOf={props.statsAsOf}
                    nearbyLocalities={props.nearbyLocalities}
                    isAmongBusiest={props.isAmongBusiest}
                />
                <div className='py-5 px-4 bg-gray-50'>
                    <h2 className='px-2 text-xl sm:text-2xl text-center uppercase'>
                        <strong>Aircon Servicing Deals In {locality}</strong>
                    </h2> 
                    <div className='max-w-5xl mx-auto space-y-5 py-5'>
                        <div className='px-2 pb-2 text-center'>
                            <select
                                className="text-lg w-fit indent-2 text-center h-12 text-orange-600 bg-white shadow-lg border rounded-xl focus:outline-none focus:shadow-outline focus:shadow-md focus:border-amber-200 focus:shadow-amber-200 focus:ring-1 ring-amber-200"
                                value={selectedService}
                                onChange={(e) => setSelectedService(e.target.value)} // Update selected service
                            >
                                {AIRCON_DEAL_FILTERS.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                        </div>
                        <AirconDealItem showJobScope={showJobScope} setRequestForm={setRequestForm} setIsQuickBook={setIsQuickBook} setIsCustomBook={setIsCustomBook} setIsShowAirconItemSelection={setIsShowAirconItemSelection} dealsArray={dealsArray} selectedService={selectedService}/>
                    </div>
                </div>                
                <AirconCustomerReviews testimonials={testimonials} />   
                {/* The "Aircon Servicing in {locality}" section with three templated
                    paragraphs sat here. Only the name changed between the 26 pages, and
                    everything it said (prices, hours, in-app chat) is answered in this
                    page's FAQs, so it was pure duplication. The area-specific content is
                    the AirconLocalityInsights block above. */}
                {/* The app-promo block that sat here is removed: a whileInView 3D
                    rotate on every one of the 26 locality pages, wrapping generic copy
                    that never named the locality, claimed "24/7" service against the
                    8am-10pm hours stated in the FAQs, and cost roughly a screen of mobile
                    fold. The pricing link it contained is kept. */}
                <section className='py-5 max-w-5xl mx-auto'>
                    <div className='text-center py-6'>
                        <Link href="/sg/aircon-servicing-singapore/aircon-servicing-price-singapore" prefetch={false} className='px-6 py-4 rounded-full bg-linear-to-tr from-orange-600 to-amber-400 text-white'>
                            Aircon Servicing Price
                        </Link>
                    </div>
                </section> 
                {/* AirconServices, AirconProblems and AirconWhyUs used to follow here, and
                    AirconBrandsWeSupport and the regulations card further down. All five
                    are the hub's content, identical on all 26 locality pages - most of
                    what made these pages ~92% alike. They stay on the hub, one click
                    away; AirconQuickLinks at the bottom keeps every service linked. */}
                {isShowAirconItemSelection &&
                    <AnimatePresence>
                        <AirconItemSelection setIsShowAirconItemSelection={setIsShowAirconItemSelection} setRequestForm={setRequestForm} setIsCustomBook={setIsCustomBook} setIsQuickBook={setIsQuickBook} userId={null}/> 
                    </AnimatePresence>
                }  
            </RequestProvider>
            {/* Discounts & Rewards and SecurePayment removed: both are generic, repeated
                verbatim on all 26 locality pages. Between them they cost roughly a full
                phone screen of fold before the locality-specific content. */}
            {/* All 26 locality pages link to each other, so the cluster is no longer
                orphaned - previously each page's only internal links were the generic
                service grid. */}
            <AcServiceLocality localityList={localityList}/>
            <section className="py-6 md:py-10">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="bg-white rounded-2xl border border-gray-100 p-5 md:p-6">
                        <div className="flex items-center gap-2.5 mb-3">
                            <HiShieldCheck className="text-orange-500 text-xl shrink-0" />
                            <h2 className="text-lg md:text-xl font-extrabold text-gray-900 tracking-tight">
                                Who services your aircon in {locality}
                            </h2>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">
                            Every aircon job in {locality} follows the service steps and quality standards StringsSG
                            defines, and we take responsibility for the quality of the service - so customers in{' '}
                            {locality} get the same service as everywhere else in Singapore. Our technicians are vetted
                            and licence checked before they take StringsSG jobs.{' '}
                            <Link href="/sg/aircon-servicing-singapore/how-stringssg-works" prefetch={false} className="text-orange-600 hover:underline">
                                How StringsSG aircon servicing works
                            </Link>
                        </p>
                    </div>
                </div>
            </section>
            <section className='max-w-5xl mx-auto py-10 px-2' aria-label="Frequently Asked Questions"> 
                <Faq
                    questionsAnswers={buildLocalityFaqs(locality, localityStats)}
                    title={`Aircon Servicing FAQs - ${locality}`}
                />
            </section>    
            {/* BlogsList stays off until the WP post fetch is added to getStaticProps. */}
            <AirconQuickLinks/>
        </div>
                            
        {isShowJobScope &&
            <AnimatePresence>
                <AirconScope 
                setIsShowJobScope={setIsShowJobScope}
                serviceType={serviceType} 
                unit={unit} 
                unitType={unitType} 
                cost={cost}/>
            </AnimatePresence>
        }
        {isLoading &&
            <LoadingCover/>
        }          
        <AnimatePresence>
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        </AnimatePresence>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}
